// Shared "time ago" formatter — e.g. "hace 3 días" / "hace 2 semanas" /
// "ayer" — extracted out of two independent hand-rolled copies:
//   - apps/web-svelte/src/routes/cartera/+page.svelte's `relativeAgo`
//     (cartera card's "última <X>" line)
//   - apps/web-dashboard/src/lib/util.ts's `lastSeenLabel`
//     (Equipo table's "last seen" column)
// Both built the same day/week/month/year ladder from scratch with
// string concatenation and `=== 1` ternaries for singular/plural. This
// is the single implementation (#0388); it is deliberately NOT
// barrel-exported from ./index.ts — import it from the
// "@bitacorapro/shared/relative-time" subpath instead. That keeps it
// reachable from bundles (like the web-svelte PWA's) that avoid the
// package root specifically to dodge the zod/pino dependencies pulled
// in by the other barrel modules (see apps/web-svelte/src/lib/forms/
// rut.ts's comment on the same tradeoff).
const DAY_MS = 86_400_000;
/**
 * Format `iso` as a coarse "time ago" string relative to `now`:
 * "hoy", "ayer", "hace 3 días", "hace 2 semanas", "hace 4 meses",
 * "hace 1 año".
 *
 * `now` is a required parameter — never read from `Date.now()`/`new
 * Date()` internally — so the function stays pure and testable
 * without clock mocking, and so a future caller can format "relative
 * to sign time" instead of "relative to render time" for a frozen,
 * signed document.
 *
 * `locale` defaults to "es-CL" (the app's canonical source locale) but
 * is an explicit parameter so a second locale is a one-line change at
 * the call site, not a rewrite of this function.
 *
 * Returns "" for a null/undefined/unparseable `iso` — callers that
 * need a different empty-state word ("Nunca", "—", …) supply it
 * themselves around this call; that word is call-site UX, not a
 * property of "how long ago".
 *
 * Today/yesterday come from `Intl.RelativeTimeFormat`'s own
 * `numeric: "auto"` CLDR data (day 0 → "hoy", day -1 → "ayer") rather
 * than hardcoded literals. A day value of exactly 1 always resolves to
 * "ayer", not "hace 1 día" — preserving the UX of both legacy
 * implementations, which never emitted the numeral form for a
 * one-day gap. Week/month/year use `numeric: "always"` instead of
 * "auto" so the phrasing stays a trailing clause ("última hace 2
 * semanas") rather than a headline ("la semana pasada"), which reads
 * oddly stitched into the existing call sites' sentences.
 */
export function formatRelativeAgo(iso, now, locale = "es-CL") {
    if (!iso)
        return "";
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then))
        return "";
    const nowMs = now instanceof Date ? now.getTime() : now;
    const days = Math.floor((nowMs - then) / DAY_MS);
    // Days <= 1 (including a future/invalid-clock negative gap, clamped
    // to "hoy" exactly like both legacy implementations did) go through
    // "auto" so the day unit's 0/-1 CLDR special cases do the "hoy"/
    // "ayer" work instead of a hand-written literal.
    if (days <= 1) {
        const rtfAuto = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
        return rtfAuto.format(days <= 0 ? 0 : -1, "day");
    }
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
    if (days < 7)
        return rtf.format(-days, "day");
    if (days < 30)
        return rtf.format(-Math.round(days / 7), "week");
    if (days < 365)
        return rtf.format(-Math.round(days / 30), "month");
    return rtf.format(-Math.round(days / 365), "year");
}
//# sourceMappingURL=relative-time.js.map